import React, { createContext, useContext, useCallback, useRef, useEffect, ReactNode, useState } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useToastContext } from './ToastContext';
import { useAuth } from './AuthContext';

interface BatchExportUpdateMessage {
  type: 'BATCH_UPDATE' | 'BATCH_COMPLETED' | 'BATCH_FAILED';
  batchId: number;
  batchNumber: string;
  status: string;
  progress?: {
    processedRecords: number;
    totalRecords: number;
  };
  errorMessage?: string;
  timestamp?: number;
}

interface BatchExportWebSocketContextType {
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  lastBatchUpdate?: BatchExportUpdateMessage;
  processingBatchCount: number;
  subscribe: (callback: (message: BatchExportUpdateMessage) => void) => () => void;
}

const BatchExportWebSocketContext = createContext<BatchExportWebSocketContextType | undefined>(undefined);

interface BatchExportWebSocketProviderProps {
  children: ReactNode;
}

export const BatchExportWebSocketProvider: React.FC<BatchExportWebSocketProviderProps> = ({ children }) => {
  const { showSuccess, showError, showInfo } = useToastContext();
  const { accessToken } = useAuth();
  const subscribersRef = useRef<Set<(message: BatchExportUpdateMessage) => void>>(new Set());
  const lastUpdateRef = useRef<BatchExportUpdateMessage | undefined>();
  const processedMessagesRef = useRef<Map<string, number>>(new Map());
  const [processingBatchCount, setProcessingBatchCount] = useState(0);
  const processingBatchesRef = useRef<Set<number>>(new Set());
  const notifiedCompletionBatchesRef = useRef<Set<number>>(new Set());
  
  // Cleanup old processed messages every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const thirtySecondsAgo = now - 30000;
      
      // Remove messages older than 30 seconds
      const keysToDelete: string[] = [];
      processedMessagesRef.current.forEach((timestamp, key) => {
        if (timestamp < thirtySecondsAgo) {
          keysToDelete.push(key);
        }
      });
      
      keysToDelete.forEach(key => {
        processedMessagesRef.current.delete(key);
      });
      
      // Clean up old notification tracking (keep only recent batch IDs to prevent memory leaks)
      // We'll keep tracking for 5 minutes to handle any late duplicate messages
      const fiveMinutesAgo = now - 300000;
      const currentNotifications = new Set(notifiedCompletionBatchesRef.current);
      
      // This is a simple cleanup - in a real app you might want to track timestamps per batch
      // For now, we'll just clear the set periodically to prevent unbounded growth
      if (currentNotifications.size > 50) {
        notifiedCompletionBatchesRef.current.clear();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const handleMessage = useCallback((message: any) => {
    if (message.type && message.type.startsWith('BATCH_')) {
      const batchMessage = message.data as BatchExportUpdateMessage;
      
      // Create different deduplication strategies based on message type
      let messageKey: string;
      let deduplicationWindow: number;
      
      if (batchMessage.type === 'BATCH_COMPLETED' || batchMessage.type === 'BATCH_FAILED') {
        // For completion/failure messages, use just type and batchId (ignore status)
        // and use a longer deduplication window since these are final states
        messageKey = `${batchMessage.type}-${batchMessage.batchId}`;
        deduplicationWindow = 30000; // 30 seconds
      } else {
        // For update messages, include status for more granular deduplication
        messageKey = `${batchMessage.type}-${batchMessage.batchId}-${batchMessage.status}`;
        deduplicationWindow = 5000; // 5 seconds
      }
      
      const now = Date.now();
      
      // Check if we've seen this message recently
      const lastProcessed = processedMessagesRef.current.get(messageKey);
      if (lastProcessed && (now - lastProcessed) < deduplicationWindow) {
        console.log('Duplicate message filtered:', messageKey, 'last processed:', new Date(lastProcessed));
        return; // Skip duplicate message
      }
      
      // Mark message as processed
      processedMessagesRef.current.set(messageKey, now);
      
      // Add timestamp if not present
      batchMessage.timestamp = batchMessage.timestamp || now;
      
      // Update last message
      lastUpdateRef.current = batchMessage;
      
      // Notify all subscribers
      subscribersRef.current.forEach(callback => {
        try {
          callback(batchMessage);
        } catch (error) {
          console.error('Error in batch export subscriber:', error);
        }
      });
      
      // Update processing batch tracking
      switch (batchMessage.type) {
        case 'BATCH_UPDATE':
          if (batchMessage.status === 'PROCESSING' || batchMessage.status === 'PENDING') {
            processingBatchesRef.current.add(batchMessage.batchId);
            setProcessingBatchCount(processingBatchesRef.current.size);
          }
          // Optional: Show progress updates for long-running batches
          if (batchMessage.progress) {
            const { processedRecords, totalRecords } = batchMessage.progress;
            const percentage = Math.round((processedRecords / totalRecords) * 100);
            
            // Only show progress notifications at certain intervals to avoid spam
            if (percentage % 25 === 0 && percentage > 0 && percentage < 100) {
              showInfo(`Batch export ${batchMessage.batchNumber} is ${percentage}% complete`);
            }
          }
          break;
          
        case 'BATCH_COMPLETED':
          processingBatchesRef.current.delete(batchMessage.batchId);
          setProcessingBatchCount(processingBatchesRef.current.size);
          
          // Only show toast if we haven't already notified about this completion
          if (!notifiedCompletionBatchesRef.current.has(batchMessage.batchId)) {
            notifiedCompletionBatchesRef.current.add(batchMessage.batchId);
            showSuccess(`Batch export ${batchMessage.batchNumber} completed successfully!`);
          }
          break;
          
        case 'BATCH_FAILED':
          processingBatchesRef.current.delete(batchMessage.batchId);
          setProcessingBatchCount(processingBatchesRef.current.size);
          
          // Only show toast if we haven't already notified about this failure
          if (!notifiedCompletionBatchesRef.current.has(batchMessage.batchId)) {
            notifiedCompletionBatchesRef.current.add(batchMessage.batchId);
            showError(`Batch export ${batchMessage.batchNumber} failed: ${batchMessage.errorMessage || 'Unknown error'}`);
          }
          break;
      }
    }
  }, [showSuccess, showError, showInfo]);

  const webSocket = useWebSocket('/ws/batch-exports', {
    onMessage: handleMessage,
    onOpen: () => {
      console.log('Connected to batch export updates (global)');
    },
    onClose: () => {
      console.log('Disconnected from batch export updates (global)');
    },
    onError: (error) => {
      console.error('WebSocket error for batch exports (global):', error);
    },
    reconnectAttempts: 5,
    reconnectInterval: 3000
  });

  const subscribe = useCallback((callback: (message: BatchExportUpdateMessage) => void) => {
    subscribersRef.current.add(callback);
    
    // Return unsubscribe function
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  const contextValue: BatchExportWebSocketContextType = {
    isConnected: webSocket.isConnected,
    connectionStatus: webSocket.connectionStatus,
    lastBatchUpdate: lastUpdateRef.current,
    processingBatchCount,
    subscribe
  };

  // Only render provider if user is authenticated
  if (!accessToken) {
    return <>{children}</>;
  }

  return (
    <BatchExportWebSocketContext.Provider value={contextValue}>
      {children}
    </BatchExportWebSocketContext.Provider>
  );
};

export const useBatchExportWebSocket = () => {
  const context = useContext(BatchExportWebSocketContext);
  if (!context) {
    // Return a dummy context when not authenticated or provider not available
    return {
      isConnected: false,
      connectionStatus: 'disconnected' as const,
      lastBatchUpdate: undefined,
      processingBatchCount: 0,
      subscribe: () => () => {}
    };
  }
  return context;
};